import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Heart, Phone, X, Shield, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SafePostingModalProps {
  open: boolean
  onClose: () => void
  ephemeralChatId?: string
  moderationStatus?: 'removed' | 'shadow' | 'review' | 'safe'
  reason?: string
  onOpenChat?: (chatId: string) => void
}

export default function SafePostingModal({ open, onClose, ephemeralChatId, moderationStatus, reason, onOpenChat }: SafePostingModalProps) {
  const { t } = useTranslation()
  const [showResources, setShowResources] = useState(false)

  if (!open) return null

  // Show ephemeral invitation if AI detected crisis
  if (ephemeralChatId) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md p-6 rounded-2xl bg-card border border-border shadow-xl">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-primary" />
            </div>

            <h2 className="text-xl font-semibold mb-2">{t('moderation.invitation.title')}</h2>
            <p className="text-sm text-muted-foreground mb-6">
              {t('moderation.invitation.subtitle')}
            </p>

            <div className="w-full p-3 rounded-lg bg-secondary/40 border border-border mb-4">
              <p className="text-xs text-muted-foreground text-center">
                🔒 {t('moderation.invitation.anonymous')}
              </p>
            </div>

            <div className="w-full space-y-2">
              {ephemeralChatId && onOpenChat && (
                <Button
                  className="w-full"
                  onClick={() => onOpenChat(ephemeralChatId)}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  {t('moderation.invitation.support')}
                </Button>
              )}

              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setShowResources(!showResources)}
              >
                <Phone className="w-4 h-4 mr-2" />
                {t('moderation.invitation.resources')}
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={onClose}
              >
                {t('moderation.invitation.later')}
              </Button>
            </div>

            {showResources && (
              <div className="w-full mt-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20 space-y-2 text-left">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-500" />
                  {t('moderation.invitation.resources')}
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p><strong>KADES:</strong> 7/24 — Kadın ve çocuk destek</p>
                  <p><strong>ALO 182:</strong> 7/24 — Sağlık Bakanlığı Ruh Sağlığı</p>
                  <p><strong>112:</strong> Acil yardım</p>
                  <a href="https://alo182.saglik.gov.tr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                    alo182.saglik.gov.tr <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Show "content removed" / "content flagged" / "safe" message
  if (moderationStatus === 'removed' || (moderationStatus as any) === 'unsafe') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md p-6 rounded-2xl bg-card border border-destructive/30 shadow-xl">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-destructive" />
            </div>

            <h2 className="text-xl font-semibold mb-2">{t('moderation.removed', { reason: reason || t('moderation.categories.spam') })}</h2>

            <p className="text-sm text-muted-foreground mb-6">
              İçeriğiniz yayınlanmamıştır. Bu konuda endişeleriniz varsa destek ekibimizle iletişime geçebilirsiniz.
            </p>

            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (moderationStatus === 'shadow' || moderationStatus === 'review') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md p-6 rounded-2xl bg-card border border-yellow-500/30 shadow-xl">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-yellow-500" />
            </div>

            <h2 className="text-xl font-semibold mb-2">{t('moderation.review')}</h2>

            <p className="text-sm text-muted-foreground mb-6">
              {t('moderation.reviewBody')}
            </p>

            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
